import React, { useState, useMemo } from "react";

import "./Project.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Card, Col, Container, Row, Button, Dropdown } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Form, Modal } from "react-bootstrap";

import { DownloadIcon } from "@components/icons";
import ProjectForm from "./Form";
import getDate from "@helper/getDate";
import { MoreIcon } from "@components/icons";
import Spinner from "@components/Spinner/Spinner";
import AddButton from "@components/Button/AddButton";
import { AddProject } from "./Button";
import ServerError from "@components/Error/ServerError";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import { CaretRightFillIcon1 } from "@components/icons";
import {
  createProjectDownloadJob,
  deleteProject,
  downloadProjectJobFile,
  getAllProjects,
  getProjectDownloadJob,
} from "../services/projectCrud";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";
import { WeatherDayAfterTommorow, WeatherToday, WeatherTommorow } from "@modules/Weather/Weather";
import { useProjectFilter } from "../../../context/ProjectFilterContext";

export default function ProjectGridView() {
  const { t } = useTranslation();
  const [openForm, setOpenForm] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedProject, setSelectedProject] = useState();
  const { selectedProjectId } = useProjectFilter();

  const scheduledGetAllProjects = createScheduledQueryFn(
    getAllProjects,
    REQUEST_PRIORITIES.HIGH,
    "getAllProjects"
  );
  const {
    data: projects,
    isLoading,
    isError,
  } = useQuery("projects", scheduledGetAllProjects, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60,
  });

  // Filter projects based on global project filter
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    // If "without-project" is selected, hide all regular projects
    if (selectedProjectId === "without-project") {
      return [];
    }

    // If a specific project is selected, only show that project
    if (selectedProjectId && selectedProjectId !== "without-project") {
      return projects.filter((project) => project.id === selectedProjectId);
    }

    // Otherwise, show all projects
    return projects;
  }, [projects, selectedProjectId]);

  // Determine whether to show the "Without Project" card
  const showWithoutProjectCard = useMemo(() => {
    // If "without-project" is selected, only show the without-project card
    if (selectedProjectId === "without-project") {
      return true;
    }

    // If a specific project is selected, hide the without-project card
    if (selectedProjectId && selectedProjectId !== "without-project") {
      return false;
    }

    // Otherwise (no filter), show the without-project card
    return true;
  }, [selectedProjectId]);

  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError) return <ServerError />;
  return (
    <>
      <Breadcrumb
        title={`${t("projects")}`}
        addButton={
          <AddButton
            label="newproject"
            action={() => {
              setIsUpdate(false);
              setSelectedProject(undefined);
              setOpenForm(true);
            }}
          />
        }
      />
      <Container data-tour="projects-page">
        <Row className="g-3">
          <Col lg={openForm ? 7 : 12}>
            {projects.length == 0 && (
              <Row>
                <Col>
                  <p>{t("start_with_adding_new_project")}</p>
                </Col>
              </Row>
            )}
            <Row className="gy-4 mb-4">
              {showWithoutProjectCard && <WithoutProjectCard />}
              <ProjectListItem
                setSelectedProject={setSelectedProject}
                setOpenForm={setOpenForm}
                setIsUpdate={setIsUpdate}
                openForm={openForm}
                projects={filteredProjects}
              />
              {!selectedProjectId && (
                <Col xl={3} lg={4} md={6}>
                  <AddProject
                    label="newproject"
                    action={() => {
                      setIsUpdate(false);
                      setSelectedProject(undefined);
                      setOpenForm(true);
                    }}
                  />
                </Col>
              )}
            </Row>
          </Col>
          {openForm && (
            <Col lg={5}>
              <div
                className="projects-inline-form-shell"
                style={{
                  height: "100%",
                  overflowY: "auto",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              >
                <ProjectForm
                  show={true}
                  isUpdate={isUpdate}
                  setOpenForm={setOpenForm}
                  setIsUpdate={setIsUpdate}
                  selectedProject={selectedProject}
                  renderMode="inline"
                />
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
}

const WithoutProjectCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate("/project/without-project/list");
  };

  return (
    <Col xl={3} lg={4} md={6}>
      <Card
        role="project"
        style={{
          borderColor: "var(--color-border-default, #d8e4ea)",
          borderStyle: "dashed",
          borderWidth: "2px",
          cursor: "pointer",
        }}
        onClick={handleClick}
      >
        <Card.Body className="d-flex justify-content-between py-2" style={{ height: "55px" }}>
          <Card.Title>
            <span className="project-name">{t("without_project")}</span>
          </Card.Title>
        </Card.Body>
        <Card.Img
          variant="top"
          src={process.env.PUBLIC_URL + "/img/project-placeholder.webp"}
          alt={t("without_project")}
          className="projectImage"
          style={{ opacity: 0.6 }}
        />
        <Card.Body className="text-end">
          <div className="d-flex align-items-center gap-1">
            <Button
              className="btn btn-outline-primary"
              variant="outline-primary"
              onClick={handleClick}
            >
              {t("viewproject")}
              <CaretRightFillIcon1 />
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

const ProjectListItem = ({ projects, setOpenForm, setIsUpdate, setSelectedProject }) => {
  const queryClient = useQueryClient();
  const { account_type } = useSelector((state) => state.auth.user);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const day = getDate();
  const [showExportModal, setShowExportModal] = useState({});

  const showProjectDetail = (project) => {
    navigate(`/project/${project.id}/map`);
  };

  const handleEdit = (project) => {
    setIsUpdate(true);
    setOpenForm(true);
    setSelectedProject(project);
  };

  const { mutateAsync } = useMutation(deleteProject, {
    onSuccess: () => {
      queryClient.invalidateQueries("projects");
    },
  });

  const handleDelete = (project) => {
    Swal.fire({
      html:
        '<p className="text-center"> ' +
        "Are you sure you want to delete project named" +
        ` <b>${project.name} </b>` +
        "?" +
        "</p>",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#388090",
      cancelButtonColor: "#EF3600",
      cancelButtonText: "Cancel",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Deleted!", "Your file has been deleted.", "success");
        mutateAsync(project.id);
      }
    });
  };

  return (
    <>
      {projects?.map((project) => {
        return (
          <>
            <Col xl={3} lg={4} md={6} key={project.id} data-tour="step-1">
              <ExportData
                project={project}
                show={showExportModal[project.id] || false}
                setShow={(value) =>
                  setShowExportModal((prev) => ({ ...prev, [project.id]: value }))
                }
                key={project.id}
              />
              <Card role="project">
                <Card.Body
                  className="d-flex justify-content-between py-2"
                  style={{ height: "55px" }}
                >
                  <Card.Title>
                    <span className="project-name">{project.name}</span>
                  </Card.Title>
                  <div className="projectWeather d-flex">
                    {project.weather && (
                      <>
                        <div className="projectStatus statusCloud me-3">
                          <WeatherToday weather={project.weather} iconWidth={"1.2rem"} />
                          <p className="mb-0 text-center ">{day.today}</p>
                          <p className="text-center">{project.weather.temperature.today}&#8451;</p>
                        </div>
                        <div className="projectStatus statusRain me-3">
                          <WeatherTommorow weather={project.weather} iconWidth={"1.2rem"} />
                          <p className="mb-0 text-center">{day.tomorrow}</p>
                          <p className="text-center">
                            {project.weather.temperature.tomorrow}&#8451;
                          </p>
                        </div>
                        <div className="projectStatus statusSun">
                          <WeatherDayAfterTommorow weather={project.weather} iconWidth={"1.2rem"} />
                          <p className="mb-0 text-center">{day.dayAfterTomorrow}</p>
                          <p className="text-center">
                            {project.weather.temperature.day_after_tomorrow}
                            &#8451;
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </Card.Body>
                <Card.Img
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = process.env.PUBLIC_URL + "/img/project-placeholder.webp";
                  }}
                  variant="top"
                  src={
                    !project.image
                      ? process.env.PUBLIC_URL + "/img/project-placeholder.webp"
                      : project?.image?.image
                  }
                  alt=""
                  className="projectImage"
                />
                <Card.Body className="text-end ">
                  <div className=" d-flex align-items-center gap-1">
                    <Button
                      data-testid="project-detail-btn"
                      className="btn  btn-outline-primary"
                      variant="outline-primary"
                      onClick={() => showProjectDetail(project)}
                    >
                      {t("viewproject")}
                      <CaretRightFillIcon1 />
                    </Button>
                    {account_type !== 2 && (
                      <>
                        <Dropdown>
                          <Dropdown.Toggle
                            data-testid="dropdown-basic"
                            className="px-2"
                            variant="outline-primary"
                            id="dropdown-basic"
                          >
                            <MoreIcon width="20px" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              data-testid="dropdown-edit"
                              onClick={() => handleEdit(project)}
                            >
                              {t("edit")}
                            </Dropdown.Item>
                            <Dropdown.Item
                              data-testid="dropdown-download"
                              onClick={() =>
                                setShowExportModal((prev) => ({ ...prev, [project.id]: true }))
                              }
                            >
                              <span>{t("download_project_csv")}</span>
                            </Dropdown.Item>
                            <Dropdown.Item
                              data-testid="dropdown-delete"
                              onClick={() => handleDelete(project)}
                            >
                              <span>{t("delete")}</span>
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </>
        );
      })}
    </>
  );
};

function ExportData({ project, setShow, show }) {
  const { t } = useTranslation();
  const { isLoading: isJobCreating, mutateAsync: createDownloadJob } =
    useMutation(createProjectDownloadJob);
  const [isPolling, setIsPolling] = useState(false);
  const [jobState, setJobState] = useState(null);

  const handleClose = () => {
    setShow(false);
  };

  const todayISO = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgoISO = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  const [startDate, setStartDate] = useState(thirtyDaysAgoISO);
  const [endDate, setEndDate] = useState(todayISO);

  const [includeMeanValues, setIncludeMeanValues] = useState(false);

  const onClick = () => {
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const runAsyncDownload = async () => {
      if (startDate && endDate && startDate > endDate) {
        Swal.fire(t("invalid_date_range") || "Start date must be before end date.");
        return;
      }

      setIsPolling(true);
      setJobState("queued");

      try {
        const job = await createDownloadJob({
          project,
          includeMeanValues,
          startDate,
          endDate,
        });
        const jobId = job?.job_id;
        if (!jobId) {
          throw new Error("Could not create export job.");
        }

        const maxWaitMs = 15 * 60 * 1000;
        const pollingEveryMs = 2000;
        const deadline = Date.now() + maxWaitMs;

        while (Date.now() < deadline) {
          const status = await getProjectDownloadJob(jobId);
          const currentStatus = status?.status;
          setJobState(currentStatus || "running");

          if (currentStatus === "done") {
            await downloadProjectJobFile({ jobId, projectName: project.name });
            Swal.fire("Download successful.");
            setShow(false);
            return;
          }

          if (currentStatus === "failed") {
            throw new Error(status?.error || "Download failed. Please try again.");
          }

          await wait(pollingEveryMs);
        }

        throw new Error("Download is taking too long. Please try a shorter date range.");
      } catch (error) {
        Swal.fire(error?.message || "Download failed. Please try again.");
      } finally {
        setIsPolling(false);
      }
    };

    if (startDate && endDate && startDate > endDate) {
      Swal.fire(t("invalid_date_range") || "Start date must be before end date.");
      return;
    }
    runAsyncDownload();
  };

  return (
    <>
      <Modal onHide={handleClose} show={show} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("download_project_data")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {project.name}
          {t("calculated_values_wip")}
          <Form className="my-3">
            <Form.Label className="fw-semibold">{t("timeframe") || "Timeframe"}</Form.Label>

            <div className="d-flex gap-2 align-items-center">
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                aria-label="start-date"
              />
              <span>–</span>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                aria-label="end-date"
              />
            </div>

            <Form.Text>
              {t("timeframe_hint") ||
                "Leave as-is to download the last 30 days. Adjust the range to narrow your export."}
            </Form.Text>

            <div className="mt-3">
              <Form.Check
                type="checkbox"
                label={t("include_mean_values")}
                checked={includeMeanValues}
                onChange={(e) => setIncludeMeanValues(e.target.checked)}
              />
            </div>
          </Form>

          {isJobCreating || isPolling ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <small className="text-muted">
                {jobState ? `Status: ${jobState}` : t("downloading")}
              </small>
            </div>
          ) : null}
          <DownloadButton disabled={isJobCreating || isPolling} onClick={onClick} />
        </Modal.Body>
      </Modal>
    </>
  );
}

function DownloadButton({ disabled, onClick }) {
  const { t } = useTranslation();
  return (
    <div className="d-flex gap-3 align-items-center ">
      <Button
        variant="outline-primary d-flex align-items-center"
        onClick={onClick}
        disabled={disabled}
      >
        <>
          <DownloadIcon />
          {t("download")}
        </>
      </Button>
    </div>
  );
}
